import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrder } from '../OrderContext';
import Header from '../components/Header';
import { AlcoholBadge, DescriptionInfo, productDescription } from '../components/ProductInfo';
import { t, FREE_MODE } from '../i18n';
import { fetchMachineProducts, isMixedProduct, isMixedPourable, type MachineProduct } from '../api';

const ProductPlaceholder = ({ name }: { name: string }) => {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
  return (
    <div className="flex h-full w-full items-center justify-center rounded-full bg-[rgba(255,255,255,0.08)] font-display text-[1.4rem] font-bold text-[var(--muted-lt)]">
      {initials}
    </div>
  );
};

const ProductIcon = ({ product }: { product: MachineProduct }) =>
  product.image_url ? (
    <img src={product.image_url} alt="" className="block h-full w-full object-contain" />
  ) : (
    <ProductPlaceholder name={product.name} />
  );

const SelectionPage = () => {
  const navigate = useNavigate();
  const { updateOrder, resetOrder, order } = useOrder();
  const [products, setProducts] = useState<MachineProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMachineProducts()
      .then((all) => {
        // The Main Menu shows ONLY 'mixed' presets — standalone base liquids
        // belong to the Custom Mix screen. A preset is listed only when it can
        // actually be poured: it has a recipe and every recipe component has
        // enough standalone stock to cover its share of a serving.
        const catalog = new Map(all.map((p) => [p.product_id, p]));
        setProducts(
          all.filter(
            (p) => isMixedProduct(p) && p.is_enabled && isMixedPourable(p, catalog),
          ),
        );
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, []);

  const handleBack = () => navigate('/menu-type');

  const handleSelect = (product: MachineProduct) => {
    const user = order.user;
    resetOrder();
    updateOrder({
      product: {
        id: product.product_id,
        icon: product.image_url ?? '',
        name: product.name,
        desc: productDescription(product.description) ?? '',
        basePrice: (product.price_cents ?? 0) / 100,
      },
      // A Main Menu pick is a 'mixed' preset — the machine pours it from the
      // product's own recipe (components), so no custom_mix map is sent.
      orderMode: 'main',
      customMix: {},
      totalPrice: (product.price_cents ?? 0) / 100,
      user,
    });
    navigate('/qr-order');
  };

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col [animation:pageFadeIn_0.3s_ease_both]">
      <div className="noise-overlay" />
      <Header title={t.chooseYourDrink} onBack={handleBack} />

      {loading && (
        <div className="relative z-10 flex flex-1 items-center justify-center">
          <span className="text-[0.9rem] text-[var(--muted)]">Loading menu…</span>
        </div>
      )}

      {!loading && error && (
        <div className="relative z-10 flex flex-1 items-center justify-center px-6">
          <p className="text-center text-[0.9rem] text-[var(--muted)]">{error}</p>
        </div>
      )}

      {!loading && !error && products.length === 0 && (
        <div className="relative z-10 flex flex-1 items-center justify-center">
          <p className="text-center text-[0.9rem] text-[var(--muted)]">No drinks available right now.</p>
        </div>
      )}

      {!loading && !error && products.length > 0 && (
        <div className="grid flex-1 min-h-0 auto-rows-auto grid-cols-2 content-start gap-3 overflow-y-auto px-4 pb-4 pt-3 sm:auto-rows-fr sm:content-stretch sm:gap-4 sm:overflow-hidden sm:px-5 sm:pb-5 sm:pt-4">
          {products.map((p, i) => (
            <ProductCard
              key={p.product_id}
              product={p}
              index={i}
              spanFullRow={i === products.length - 1 && products.length % 2 === 1}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type ProductCardProps = {
  product: MachineProduct;
  index: number;
  spanFullRow: boolean;
  onSelect: (product: MachineProduct) => void;
};

const ProductCard = ({ product, index, spanFullRow, onSelect }: ProductCardProps) => {
  const [pressed, setPressed] = React.useState(false);
  const price = (product.price_cents ?? 0) / 100;
  const hasInfo = Boolean(productDescription(product.description));

  return (
    <div
      className={`relative h-full min-h-[8.5rem] w-full overflow-hidden rounded-[20px] border bg-[var(--card)] transition-all duration-150 ease-in-out [animation:pageFadeIn_0.4s_ease_both] sm:min-h-0 ${
        pressed
          ? 'scale-[0.97] border-[var(--gold-lt)] shadow-[0_0_32px_var(--accent-pressed-shadow)]'
          : 'scale-100 border-[var(--border)] shadow-[var(--card-shadow)]'
      } ${spanFullRow ? 'sm:col-span-2' : ''}`}
      style={{ animationDelay: `${index * 0.07}s` }}
    >
      <DescriptionInfo description={product.description}>
        <button
          type="button"
          className={`touch-target relative flex w-full flex-1 cursor-pointer flex-col items-center justify-center gap-2 px-3 py-4 text-center sm:flex-row sm:justify-start sm:gap-10 sm:px-0 sm:py-[clamp(0.6rem,2vh,2rem)] sm:pl-10 sm:pr-6 sm:text-left ${
            hasInfo ? 'pr-11 sm:pr-14' : ''
          }`}
          onPointerDown={() => setPressed(true)}
          onPointerUp={() => { setPressed(false); onSelect(product); }}
          onPointerCancel={() => setPressed(false)}
          onPointerLeave={() => setPressed(false)}
        >
          <div className={`absolute inset-x-0 top-0 h-1 rounded-t-[20px] transition-all duration-150 sm:inset-y-0 sm:right-auto sm:h-full sm:w-1 sm:rounded-l-[20px] sm:rounded-tr-none ${pressed ? 'bg-[var(--gold-lt)]' : 'bg-[var(--accent-bar)]'}`} />

          <div className="flex h-16 w-16 shrink-0 items-center justify-center [filter:drop-shadow(0_2px_14px_var(--accent-glow))] [animation:breathe_3s_ease-in-out_infinite] sm:h-[clamp(3rem,13vh,7rem)] sm:w-[clamp(3rem,13vh,7rem)]">
            <ProductIcon product={product} />
          </div>

          <div className="flex w-full min-w-0 flex-col items-center gap-1 sm:flex-1 sm:items-start sm:gap-1.5">
            <div className="line-clamp-2 font-display text-[1.05rem] font-bold leading-tight text-[var(--cream)] sm:line-clamp-none sm:text-[clamp(1.4rem,2.6vw,2.5rem)]">
              {product.name}
            </div>
            <AlcoholBadge product={product} />
            {!FREE_MODE && (
              <div className="text-[1rem] font-semibold text-[var(--gold-lt)] sm:text-[clamp(1.1rem,1.9vw,1.85rem)]">
                €{price.toFixed(2)}
              </div>
            )}
          </div>

          <div className="hidden text-[var(--gold-lt)] opacity-50 sm:block sm:w-10 sm:shrink-0 sm:text-center sm:text-[2.5rem]">›</div>
        </button>
      </DescriptionInfo>
    </div>
  );
};

export default SelectionPage;
